import { definedMap } from "collection-utils";
import { asString } from "common/components/component-helpers";
import type { ScreenFlag } from "common/components/SerializedApp";
import type { EvaluatedScreen as Screen, ValueProducer } from "common/components/types";
import { logError } from "common/debug-print";
import { assert, defined, definedMapWithDefault, panic, replaceArrayItem } from "common/Support";
import last from "lodash/last";
import type { BuilderNavigationModel } from "hydrated-ui/types";

export type NavigationAnimation = "push" | "pop" | "push-modal" | "pop-modal" | "none";

// Instances of Pivots are used to save inter-pivot shared state in WeakMaps.
// Take care to not frivolously replace them in the navigation model.
export interface Pivots {
    readonly pivots: ReadonlyArray<Screen>;
}

interface RootPivots extends Pivots {
    readonly isVisible: ValueProducer | undefined;
}

interface LocalState {
    [k: string]: Readonly<unknown>;
}

// FIXME: The key type should be an enum.
export type ScreenLocalState = LocalState;
export type PivotLocalState = LocalState;

interface ScreenWithProvenance {
    readonly screen: Screen;
}

export interface AppNavigationModel extends BuilderNavigationModel {
    readonly currentTabIndex: number;
    // If we're in a flyout tab on the phone, the `currentTabIndex` will
    // be whatever it was before the flyout tab screen was pushed, but
    // this will give the index of the flyout tab.
    readonly currentLogicalTabIndex: number;
    readonly currentScreen: Screen | undefined;
    readonly currentScreenDepth: number;
    readonly currentScreenTitle: string | undefined;
    readonly currentScreenInheritedFlags: Set<RuntimeScreenFlags>;
    readonly currentScreenLocalState: ScreenLocalState;
    readonly currentPivotIndex: number;
    readonly currentPivotLocalState: PivotLocalState;
    readonly parentScreen: ScreenWithProvenance | undefined;
    readonly modalParentScreen: ScreenWithProvenance | undefined;
    readonly canCurrentScreenPop: boolean;
    readonly canCurrentScreenPopModal: boolean;
    readonly isVisibles: readonly (ValueProducer | undefined)[];

    getAnimation(previousState: AppNavigationModel): NavigationAnimation;

    hasScreen(screen: Screen): boolean;

    pushScreen(pivots: Pivots, logicalTabIndex?: number, flags?: readonly RuntimeScreenFlags[]): AppNavigationModel;
    popScreen(): AppNavigationModel;
    popToRoot(): AppNavigationModel;
    popToLogicalTab(): AppNavigationModel;
    popModalsToRoot(): AppNavigationModel;
    pushModalScreen(pivots: Pivots): AppNavigationModel;
    popModalScreen(): AppNavigationModel;
    reorderTabs(permutation: readonly number[]): AppNavigationModel;
    addTab(pivots: RootPivots, index: number): AppNavigationModel;
    removeTab(index: number): AppNavigationModel;
    replaceTopScreens(newScreens: ReadonlyArray<Pivots>): AppNavigationModel;
    replaceScreenStack(index: number, pivots: RootPivots): AppNavigationModel;
    setTab(index: number): AppNavigationModel;
    setPivot(index: number): AppNavigationModel;
    updateScreenLocalState(updates: Partial<ScreenLocalState>): AppNavigationModel;
    updatePivotLocalState(updates: Partial<PivotLocalState>): AppNavigationModel;

    isInModal(): boolean;
    currentScreenHasFlag(flag: ScreenFlag): boolean;
    pivotLocalStateForIndex(index: number): Readonly<PivotLocalState>;

    // FIXME: roots being exposed is weird.  It's only used in the
    // UpdateTabDescription action.
    getRootForTab(tabIndex: number): Pivots;

    replaceAllScreens(f: (pivots: Pivots) => Pivots): AppNavigationModelImpl;
}

function makeTabFromPivots(pivots: RootPivots): Tab {
    return {
        modalGroupStack: [
            {
                screenStack: [
                    {
                        pivots: {
                            pivots: pivots.pivots,
                        },
                        currentPivotIndex: 0,
                        screenLocalState: {},
                        pivotLocalStates: new Array(pivots.pivots.length).fill({}),
                        flags: new Set<RuntimeScreenFlags>(),
                    },
                ],
            },
        ],
        isVisible: pivots.isVisible,
    };
}

export type AppNavigationCallbacks = Readonly<{
    onAppNavigationPush?: (navModel: AppNavigationModel) => void;
}>;

export function createNavigationModel(
    roots: ReadonlyArray<RootPivots>,
    currentTabIndex: number,
    callbacks: AppNavigationCallbacks
): AppNavigationModel {
    return new AppNavigationModelImpl(roots.map(makeTabFromPivots), currentTabIndex, currentTabIndex, callbacks);
}

// FIXME: Why are these needed?  Why not use `ScreenFlags`?
export enum RuntimeScreenFlags {
    HideTabBar,
}

interface ScreenWithPivots {
    readonly pivots: Pivots;
    readonly currentPivotIndex: number;
    readonly screenLocalState: ScreenLocalState;
    readonly pivotLocalStates: PivotLocalState[];
    readonly flags: Set<RuntimeScreenFlags>;
}

type ScreenStack = ReadonlyArray<ScreenWithPivots>;

interface ModalGroup {
    readonly screenStack: ScreenStack;
}

interface Tab {
    readonly modalGroupStack: ReadonlyArray<ModalGroup>;
    readonly isVisible: ValueProducer | undefined;
}

function screenForScreenWithPivots({ pivots, currentPivotIndex }: ScreenWithPivots): ScreenWithProvenance | undefined {
    if (currentPivotIndex >= pivots.pivots.length) return undefined;
    return { screen: pivots.pivots[currentPivotIndex] };
}

class AppNavigationModelImpl implements AppNavigationModel {
    // The model is indexed by
    // 1. tab array
    // 2. modal group stack
    // 3. screen stack within the modal
    // 4. pivots within a screen
    constructor(
        private readonly model: ReadonlyArray<Tab>,
        public readonly currentTabIndex: number,
        public readonly currentLogicalTabIndex: number,
        private readonly callbacks: AppNavigationCallbacks
    ) {
        assert(currentTabIndex >= 0 && currentTabIndex < model.length);
    }

    private get currentScreenWithPivots(): ScreenWithPivots {
        const { screenStack } = this.currentModalStack;
        assert(screenStack.length > 0);
        return screenStack[screenStack.length - 1];
    }

    public get currentScreen(): Screen | undefined {
        return screenForScreenWithPivots(this.currentScreenWithPivots)?.screen;
    }

    private get maybeCurrentScreen(): Screen | undefined {
        const { screenStack } = this.currentModalStack;
        if (screenStack.length === 0) return undefined;
        return screenForScreenWithPivots(screenStack[screenStack.length - 1])?.screen;
    }

    public get currentScreenTitle(): string | undefined {
        return definedMap(this.maybeCurrentScreen?.title?.current, asString);
    }

    private onAppNavigationPush(): AppNavigationModelImpl {
        const { onAppNavigationPush } = this.callbacks;
        if (onAppNavigationPush !== undefined) {
            onAppNavigationPush(this);
        }
        return this;
    }

    public get currentPivotIndex(): number {
        return this.currentScreenWithPivots.currentPivotIndex;
    }

    // NOTE: This is not correct.  We're just not using it now, and this is
    // the old model, so it'll go away and we don't care.
    public get isCurrentTabVisible(): boolean {
        return true;
    }

    public get currentScreenLocalState(): Readonly<ScreenLocalState> {
        const { screenLocalState } = this.currentScreenWithPivots;
        return screenLocalState;
    }

    public get currentPivotLocalState(): Readonly<PivotLocalState> {
        const { pivotLocalStates } = this.currentScreenWithPivots;
        const currentPivotIndex = this.currentPivotIndex;
        return pivotLocalStates[currentPivotIndex];
    }

    public pivotLocalStateForIndex(index: number): Readonly<PivotLocalState> {
        const { pivotLocalStates } = this.currentScreenWithPivots;
        if (index >= 0 && index < pivotLocalStates.length) {
            return pivotLocalStates[index];
        }
        return {};
    }

    public get currentScreenInheritedFlags(): Set<RuntimeScreenFlags> {
        let result = new Set<RuntimeScreenFlags>();
        const cms = this.currentModalStack;
        cms.screenStack.forEach(s => (result = new Set([...result, ...s.flags])));
        return result;
    }

    public updateScreenLocalState(newState: Partial<ScreenLocalState>): AppNavigationModelImpl {
        const { pivots, currentPivotIndex, screenLocalState, pivotLocalStates, flags } = this.currentScreenWithPivots;
        const updated: ScreenLocalState = {
            ...screenLocalState,
        };

        // I'd _love_ to just ...newState here. I can't do that.
        // newState: Partial<ScreenLocalState> = { [P in keyof ScreenLocalState]?: ScreenLocalState[P] | undefined }
        // ScreenLocalState[P] = Readonly<unknown> for any P, but undefined is not compatible with Readonly<unknown>.
        // Note that everything _else_ has to be readonly, just not undefined by that definition.
        for (const key of Object.keys(newState)) {
            updated[key] = newState[key] as Readonly<unknown>;
        }

        return this.popCurrentScreenUnchecked().pushScreenWithPivots({
            pivots,
            currentPivotIndex,
            screenLocalState: updated,
            pivotLocalStates,
            flags,
        });
    }

    public updatePivotLocalState(newState: Partial<PivotLocalState>): AppNavigationModelImpl {
        const { pivots, currentPivotIndex, screenLocalState, pivotLocalStates, flags } = this.currentScreenWithPivots;
        const pivotLocalState = pivotLocalStates[currentPivotIndex];
        const updated: PivotLocalState = {
            ...pivotLocalState,
        };

        // I'd _love_ to just ...newState here. I can't do that.
        // newState: Partial<ScreenLocalState> = { [P in keyof ScreenLocalState]?: ScreenLocalState[P] | undefined }
        // ScreenLocalState[P] = Readonly<unknown> for any P, but undefined is not compatible with Readonly<unknown>.
        // Note that everything _else_ has to be readonly, just not undefined by that definition.
        for (const key of Object.keys(newState)) {
            updated[key] = newState[key] as Readonly<unknown>;
        }

        pivotLocalStates[currentPivotIndex] = updated;

        return this.popCurrentScreenUnchecked().pushScreenWithPivots({
            pivots,
            currentPivotIndex,
            screenLocalState,
            pivotLocalStates,
            flags,
        });
    }

    public hasScreen(screen: Screen): boolean {
        return this.currentTab.modalGroupStack.some(g =>
            g.screenStack.some(p => p.pivots.pivots.some(s => s === screen))
        );
    }

    public setPivot(index: number): AppNavigationModel {
        const { screenStack } = this.currentModalStack;

        let i: number;
        let screen: ScreenWithPivots | undefined;
        for (i = 1; i <= screenStack.length; i++) {
            const s = screenStack[screenStack.length - i];
            if (s.pivots.pivots.length > 1) {
                screen = s;
                break;
            }
        }

        assert(screen !== undefined);
        assert(index >= 0 && index < screen.pivots.pivots.length);

        const newStack = replaceArrayItem(screenStack, screenStack.length - i, { ...screen, currentPivotIndex: index });

        return this.replaceCurrentStack(newStack).onAppNavigationPush();
    }

    public get parentScreen(): ScreenWithProvenance | undefined {
        const { screenStack } = this.currentModalStack;
        if (screenStack.length < 2) return undefined;
        return screenForScreenWithPivots(screenStack[screenStack.length - 2]);
    }

    public get modalParentScreen(): ScreenWithProvenance | undefined {
        if (this.canCurrentScreenPop) return undefined;

        const { modalGroupStack } = this.currentTab;
        if (modalGroupStack.length <= 1) return undefined;

        const { screenStack } = modalGroupStack[modalGroupStack.length - 2];
        assert(screenStack.length > 0);
        return screenForScreenWithPivots(screenStack[screenStack.length - 1]);
    }

    public get canCurrentScreenPop(): boolean {
        const { screenStack } = this.currentModalStack;
        return screenStack.length > 1;
    }

    public get currentScreenDepth(): number {
        const { screenStack } = this.currentModalStack;
        return screenStack.length;
    }

    public getRootForTab(tabIndex: number): Pivots {
        return defined(this.model.map(t => t.modalGroupStack[0].screenStack[0])[tabIndex]).pivots;
    }

    public getAnimation(previousState: AppNavigationModel): NavigationAnimation {
        if (!(previousState instanceof AppNavigationModelImpl)) {
            return panic("Foreign navigation model");
        }

        if (this.currentTabIndex !== previousState.currentTabIndex) return "none";

        const { modalGroupStack: currentTab } = this.currentTab;
        const { modalGroupStack: previousTab } = previousState.currentTab;

        if (currentTab.length > previousTab.length) return "push-modal";
        if (currentTab.length < previousTab.length) return "pop-modal";

        const { screenStack: currentStack } = this.currentModalStack;
        const { screenStack: previousStack } = previousState.currentModalStack;

        if (currentStack.length > previousStack.length) return "push";
        if (currentStack.length < previousStack.length) return "pop";

        return "none";
    }

    private pushScreenWithPivots(screenWithPivots: ScreenWithPivots): AppNavigationModelImpl {
        return this.replaceCurrentStack(this.currentModalStack.screenStack.concat([screenWithPivots]));
    }

    public pushScreen(
        pivots: Pivots,
        logicalTabIndex: number | undefined,
        flags?: readonly RuntimeScreenFlags[],
        elideCallbacks?: boolean
    ): AppNavigationModelImpl {
        let ret = this.pushScreenWithPivots({
            pivots,
            currentPivotIndex: 0,
            screenLocalState: {},
            pivotLocalStates: new Array(pivots.pivots.length).fill({}),
            flags: new Set(flags),
        });
        if (logicalTabIndex !== undefined) {
            ret = new AppNavigationModelImpl(ret.model, ret.currentTabIndex, logicalTabIndex, ret.callbacks);
        }
        if (elideCallbacks !== true) {
            ret.onAppNavigationPush();
        }
        return ret;
    }

    public pushModalScreen(pivots: Pivots): AppNavigationModel {
        const currentTab = this.currentTab;
        const modalGroupStack = currentTab.modalGroupStack.concat([
            {
                screenStack: [
                    {
                        pivots,
                        currentPivotIndex: 0,
                        screenLocalState: {},
                        pivotLocalStates: new Array(pivots.pivots.length).fill({}),
                        flags: new Set<RuntimeScreenFlags>(),
                    },
                ],
            },
        ]);
        return this.replaceCurrentTab({ modalGroupStack, isVisible: currentTab.isVisible });
    }

    public popScreen(): AppNavigationModel {
        const { screenStack } = this.currentModalStack;
        if (screenStack.length <= 1) {
            logError("Tried to pop screen, but already at root", this);
            return this;
        }

        return this.popCurrentScreenUnchecked();
    }

    public popToRoot(): AppNavigationModel {
        const { screenStack } = this.currentModalStack;
        assert(screenStack.length > 0);
        const newStack = [screenStack[0]];

        return this.replaceCurrentStack(newStack);
    }

    public popToLogicalTab(): AppNavigationModel {
        let result = this.popModalsToRoot();
        const tabIndex = result.currentLogicalTabIndex;
        while (result.canCurrentScreenPop && result.popScreen().currentLogicalTabIndex === tabIndex) {
            result = result.popScreen();
        }
        return result;
    }

    public get canCurrentScreenPopModal(): boolean {
        const { modalGroupStack } = this.currentTab;
        return modalGroupStack.length > 1;
    }

    public popModalsToRoot(): AppNavigationModel {
        const { modalGroupStack, isVisible } = this.currentTab;
        if (modalGroupStack.length <= 1) {
            return this;
        }

        const newTab = [modalGroupStack[0]];
        return this.replaceCurrentTab({ modalGroupStack: newTab, isVisible });
    }

    public popModalScreen(): AppNavigationModel {
        const { modalGroupStack, isVisible } = this.currentTab;
        if (modalGroupStack.length <= 1) {
            logError("Tried to pop modal, but already at root", this);
            return this;
        }

        const newTab = modalGroupStack.slice(0, modalGroupStack.length - 1);
        return this.replaceCurrentTab({ modalGroupStack: newTab, isVisible });
    }

    public replaceTopScreens(newScreens: ReadonlyArray<Pivots>): AppNavigationModel {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let newNavModel: AppNavigationModelImpl = this;
        const flags: RuntimeScreenFlags[][] = [];
        const logicalTabIndices: number[] = [];
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (const _s of newScreens) {
            flags.push(
                definedMapWithDefault(last(newNavModel.currentModalStack.screenStack), [], s => Array.from(s.flags))
            );
            logicalTabIndices.push(newNavModel.currentLogicalTabIndex);

            newNavModel = newNavModel.popCurrentScreenUnchecked();
        }
        for (const s of newScreens) {
            newNavModel = newNavModel.pushScreen(s, logicalTabIndices.pop(), defined(flags.pop()), true);
        }

        return newNavModel;
    }

    public replaceScreenStack(index: number, pivots: RootPivots): AppNavigationModel {
        assert(index < this.model.length);

        const newModel = replaceArrayItem(this.model, index, makeTabFromPivots(pivots));
        return new AppNavigationModelImpl(newModel, this.currentTabIndex, this.currentLogicalTabIndex, this.callbacks);
    }

    public reorderTabs(permutation: readonly number[]): AppNavigationModel {
        const newModel: Tab[] = [];
        let newIndex = this.currentTabIndex;
        for (const from of permutation) {
            // I personally think these are sent backwards so I named them that way
            // to is the new location where the tab should go, from is its current location
            // in the array. Dont ask me why they come the other way normally...

            // FIXME: The provenance has to be fixed here, too.
            const tab = this.model[from];
            if (tab === undefined) continue;

            newModel.push(tab);

            if (this.currentTabIndex === from) {
                newIndex = newModel.length - 1;
            }
        }

        return new AppNavigationModelImpl(newModel, newIndex, newIndex, this.callbacks);
    }

    public addTab(pivots: RootPivots, index: number): AppNavigationModel {
        const oldModel = this.model;
        assert(index >= 0 && index <= oldModel.length);
        const newModel = [...oldModel.slice(0, index), makeTabFromPivots(pivots), ...oldModel.slice(index)];
        return new AppNavigationModelImpl(newModel, index, index, this.callbacks);
    }

    public removeTab(index: number): AppNavigationModel {
        const oldModel = this.model;
        assert(index >= 0 && index < oldModel.length && oldModel.length > 1);
        const newModel = [...oldModel.slice(0, index), ...oldModel.slice(index + 1)];
        const newTabIndex = Math.min(this.currentTabIndex, newModel.length - 1);
        return new AppNavigationModelImpl(newModel, newTabIndex, newTabIndex, this.callbacks);
    }

    public setTab(index: number): AppNavigationModel {
        return new AppNavigationModelImpl(this.model, index, index, this.callbacks).onAppNavigationPush();
    }

    public isInModal(): boolean {
        const { modalGroupStack } = this.currentTab;
        return modalGroupStack.length > 1;
    }

    public currentScreenHasFlag(flag: ScreenFlag): boolean {
        const { currentScreen } = this;
        if (currentScreen === undefined) return false;
        return currentScreen.flags.has(flag);
    }

    public get isVisibles(): readonly (ValueProducer | undefined)[] {
        return this.model.map(m => m.isVisible);
    }

    private getTab(index: number): Tab {
        assert(index < this.model.length);
        return this.model[index];
    }

    private get currentTab(): Tab {
        return this.getTab(this.currentTabIndex);
    }

    private get currentModalStack(): ModalGroup {
        const { modalGroupStack } = this.currentTab;
        assert(modalGroupStack.length > 0);
        return modalGroupStack[modalGroupStack.length - 1];
    }

    private replaceCurrentTab(newTab: Tab): AppNavigationModelImpl {
        const newModel = replaceArrayItem(this.model, this.currentTabIndex, newTab);

        const atBottom = newTab.modalGroupStack.length === 1 && newTab.modalGroupStack[0].screenStack.length <= 1;
        const newLogicalTabIndex = atBottom ? this.currentTabIndex : undefined;

        return new AppNavigationModelImpl(
            newModel,
            this.currentTabIndex,
            newLogicalTabIndex ?? this.currentLogicalTabIndex,
            this.callbacks
        );
    }

    private replaceCurrentStack(newStack: ScreenStack): AppNavigationModelImpl {
        const { modalGroupStack, isVisible } = this.currentTab;
        assert(modalGroupStack.length > 0);
        const newTab = modalGroupStack.slice(0, modalGroupStack.length - 1);
        newTab.push({ screenStack: newStack });

        return this.replaceCurrentTab({ modalGroupStack: newTab, isVisible });
    }

    private popCurrentScreenUnchecked(): AppNavigationModelImpl {
        const { screenStack } = this.currentModalStack;
        assert(screenStack.length > 0);
        const newStack = screenStack.slice(0, screenStack.length - 1);

        return this.replaceCurrentStack(newStack);
    }

    public replaceAllScreens(f: (pivots: Pivots) => Pivots): AppNavigationModelImpl {
        return new AppNavigationModelImpl(
            this.model.map(tab => ({
                ...tab,
                modalGroupStack: tab.modalGroupStack.map(mgs => ({
                    ...mgs,
                    screenStack: mgs.screenStack.map(ss => ({
                        ...ss,
                        pivots: f(ss.pivots),
                    })),
                })),
            })),
            this.currentTabIndex,
            this.currentLogicalTabIndex,
            this.callbacks
        );
    }
}

export function isAppNavigationModel(navModel: BuilderNavigationModel): navModel is AppNavigationModel {
    return navModel instanceof AppNavigationModelImpl;
}
